
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../types';
import { 
  auth, 
  db, 
  OAuthProvider, 
  signInWithPopup,
  updateEmail,
  updateProfile,
  doc, 
  getDoc, 
  setDoc 
} from '../firebase';

const UserLogin: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // --- 소셜 로그인 추가 정보 입력을 위한 상태 ---
  const [isSocialCompleting, setIsSocialCompleting] = useState(false);
  const [socialFormData, setSocialFormData] = useState({
    uid: '',
    provider: '',
    name: '',
    email: '',
    phone: '',
    address: '',
    detailAddress: ''
  });

  const formatPhoneNumber = (value: string) => {
    if (!value) return value;
    const phoneNumber = value.replace(/[^\d]/g, '');
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 8) {
      return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
    }
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 7)}-${phoneNumber.slice(7, 11)}`;
  };

  const handleAddressSearch = () => {
    new window.daum.Postcode({
      oncomplete: (data: any) => {
        setSocialFormData(prev => ({ ...prev, address: data.address }));
        setTimeout(() => {
          document.getElementById('socialDetailAddress')?.focus();
        }, 100);
      }
    }).open();
  };

  const handleSocialLogin = async (providerName: string) => {
    if (isLoading) return;
    setIsLoading(true);
    
    // 카카오와 네이버 모두 OIDC 제공업체로 등록된 ID를 사용합니다.
    const providerId = providerName === '카카오' ? 'oidc.kakao' : 'oidc.naver';
    const provider = new OAuthProvider(providerId);

    // OIDC 필수 스코프 설정
    if (providerName === '네이버') {
      provider.addScope('openid');
      provider.addScope('profile');
      provider.addScope('email');
    }

    try {
      const result = await signInWithPopup(auth, provider);
      const firebaseUser = result.user;

      const userDocRef = doc(db, "users", firebaseUser.uid);
      const userDoc = await getDoc(userDocRef);

      // [핵심] 최초 로그인 유저라면 추가 정보 입력 폼으로 유도
      if (!userDoc.exists()) {
        setSocialFormData({
          uid: firebaseUser.uid,
          provider: providerName,
          name: firebaseUser.displayName || '',
          email: firebaseUser.email || '',
          phone: '',
          address: '',
          detailAddress: ''
        });
        setIsSocialCompleting(true);
      } else {
        alert(`${providerName} 계정으로 로그인되었습니다.`);
        // [수정] 인트로 대신 쇼핑홈으로
        navigate('/shop', { replace: true });
      }
    } catch (error: any) {
      console.error(`${providerName} Social Login Error:`, error);
      
      let errorMsg = `${providerName} 로그인 중 오류가 발생했습니다.`;
      
      if (error.code === 'auth/operation-not-allowed') {
        errorMsg = `[관리자 설정 필요]\n1. Firebase 콘솔 > Authentication > Sign-in method\n2. 'OpenID Connect' 추가\n3. 제공업체 ID를 '${providerId}'로 설정하세요.`;
      } else if (error.code === 'auth/popup-closed-by-user') {
        setIsLoading(false);
        return;
      } else if (error.code === 'auth/invalid-provider-id') {
        errorMsg = `제공업체 ID(${providerId})가 유효하지 않습니다. Firebase 설정을 확인하세요.`;
      }
      
      alert(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompleteSocialSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    
    if (!socialFormData.email || !socialFormData.name || !socialFormData.phone || !socialFormData.address) {
      alert('성함, 이메일, 연락처, 주소를 모두 입력해주세요.');
      return;
    }

    setIsLoading(true);
    try {
      if (auth.currentUser) {
        try {
          await updateProfile(auth.currentUser, { displayName: socialFormData.name });
          if (socialFormData.email && auth.currentUser.email !== socialFormData.email) {
            await updateEmail(auth.currentUser, socialFormData.email);
          }
        } catch (authError: any) {
          console.warn("Auth Info Update partial fail:", authError);
        }
      }

      const userData: User = {
        id: socialFormData.uid,
        name: socialFormData.name,
        email: socialFormData.email,
        password: '', 
        phone: socialFormData.phone,
        address: socialFormData.address,
        detailAddress: socialFormData.detailAddress,
        points: 3000, 
        joinedAt: new Date().toISOString()
      };

      await setDoc(doc(db, "users", socialFormData.uid), userData);
      alert(`반갑습니다, ${socialFormData.name}님!\n가입 축하 3,000포인트가 지급되었습니다.`);
      // [수정] 가입 완료 후 바로 쇼핑 페이지로 리다이렉트
      window.location.assign(window.location.origin + '/shop');
    } catch (error: any) {
      console.error("Social Completion Error:", error);
      alert('정보 저장 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // 추가 정보 입력 UI (최초 가입 시에만 노출)
  if (isSocialCompleting) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center bg-gray-50 px-4 py-12">
        <div className="bg-white p-10 md:p-12 rounded-[56px] shadow-2xl max-w-md w-full border border-gray-100 animate-in zoom-in-95 duration-500">
          <div className="text-center mb-10">
            <div className="w-20 h-20 bg-indigo-600 rounded-[24px] flex items-center justify-center mx-auto mb-6 text-white font-black text-3xl shadow-2xl shadow-indigo-100 transform -rotate-6">
              M
            </div>
            <h2 className="text-2xl font-black text-gray-900 tracking-tighter italic uppercase">상품 수령 정보</h2>
            <p className="text-gray-400 mt-2 font-medium italic">{socialFormData.provider} 계정 연동을 위해 상품 배송 정보를 입력해주세요.</p>
          </div>

          <form onSubmit={handleCompleteSocialSignup} className="space-y-5">
            <div>
              <label className="block text-[10px] font-black text-gray-400 mb-2 uppercase tracking-widest">수령인 성함<span className="text-red-500">*</span></label>
              <input 
                type="text" required
                value={socialFormData.name}
                onChange={e => setSocialFormData({...socialFormData, name: e.target.value})}
                className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-indigo-600 outline-none font-black"
                placeholder="실명을 입력해주세요"
              />
            </div>
            <div>
              <label className="block text-[10px] font-black text-gray-400 mb-2 uppercase tracking-widest">이메일 주소<span className="text-red-500">*</span></label>
              <input 
                type="email" required
                value={socialFormData.email}
                onChange={e => setSocialFormData({...socialFormData, email: e.target.value})}
                className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-indigo-600 outline-none font-black"
                placeholder="example@makerstory.com"
              />
            </div>
            <div>
              <label className="block text-[10px] font-black text-gray-400 mb-2 uppercase tracking-widest">휴대폰 번호<span className="text-red-500">*</span></label>
              <input 
                type="tel" required
                maxLength={13}
                value={socialFormData.phone}
                onChange={e => setSocialFormData({...socialFormData, phone: formatPhoneNumber(e.target.value)})}
                className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-indigo-600 outline-none font-black"
                placeholder="010-0000-0000"
              />
            </div>
            <div className="space-y-3">
              <label className="block text-[10px] font-black text-gray-400 mb-1 uppercase tracking-widest">배송지 주소<span className="text-red-500">*</span></label>
              <div className="flex gap-2">
                <input 
                  type="text" readOnly required
                  value={socialFormData.address}
                  onClick={handleAddressSearch}
                  className="flex-1 px-6 py-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-indigo-600 outline-none font-black text-sm cursor-pointer"
                  placeholder="우편번호 찾기 버튼을 클릭하세요"
                />
                <button type="button" onClick={handleAddressSearch} className="px-6 py-4 bg-gray-900 text-white font-black text-xs rounded-2xl hover:bg-indigo-600 transition shadow-xl">찾기</button>
              </div>
              <input 
                type="text" id="socialDetailAddress"
                value={socialFormData.detailAddress}
                onChange={e => setSocialFormData({...socialFormData, detailAddress: e.target.value})}
                className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-indigo-600 outline-none font-black text-sm"
                placeholder="건물명, 동/호수 등 상세 주소를 입력하세요"
              />
            </div>

            <button 
              type="submit"
              disabled={isLoading}
              className="w-full py-5 bg-indigo-600 text-white font-black rounded-[28px] hover:bg-gray-900 transition transform active:scale-95 shadow-2xl shadow-indigo-100 mt-4 text-lg"
            >
              {isLoading ? '저장 중...' : '회원가입 완료 및 로그인'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center bg-gray-50 px-4 py-12">
      <div className="bg-white p-10 md:p-12 rounded-[56px] shadow-2xl max-w-md w-full border border-gray-100 animate-in zoom-in-95 duration-500">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center mx-auto mb-4 text-indigo-600">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
            </svg>
          </div>
          <h2 className="text-2xl font-black text-gray-900 tracking-tight italic uppercase">Login</h2>
          <p className="text-sm text-gray-400 mt-2 font-medium">소셜 계정으로 1초 만에 시작하세요.</p>
        </div>
        
        <div className="space-y-3">
          <button 
            type="button"
            disabled={isLoading}
            onClick={() => handleSocialLogin('카카오')}
            className="w-full py-3.5 bg-[#FEE500] text-[#191919] font-black rounded-2xl flex items-center justify-center gap-3 hover:brightness-95 transition active:scale-95 text-xs shadow-sm disabled:opacity-50"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
              <path d="M12 3c-4.97 0-9 3.185-9 7.115 0 2.558 1.712 4.8 4.312 6.095l-1.09 3.993c-.056.206.19.378.358.26l4.734-3.136c.224.015.451.023.686.023 4.97 0 9-3.185 9-7.115S16.97 3 12 3z" />
            </svg>
            카카오 로그인
          </button>
          <button 
            type="button"
            disabled={isLoading}
            onClick={() => handleSocialLogin('네이버')}
            className="w-full py-3.5 bg-[#03C75A] text-white font-black rounded-2xl flex items-center justify-center gap-3 hover:brightness-95 transition active:scale-95 text-xs shadow-sm disabled:opacity-50"
          >
            <div className="w-5 h-5 flex items-center justify-center font-black text-sm">N</div>
            네이버 로그인
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserLogin;
